import React from "react";
import PropTypes from "prop-types";
import Card from "components/Card";
import { useProfile } from "hooks";
import {
  ProfileDisciplines,
  ProfileSkills,
  ProfileContact,
  ProfileEducation,
  ProfileBio,
  ProfileWorkExperience,
  ProfileAiSummary,
  ProfileNoteAiSummary,
} from "components/Profile";
import { MessagesError } from "components/Messages";
import ProfileResume from "./ProfileResume";
import { Grid } from "components/Containers";

/**
 * Profile Box
 *
 * @param {Number}   id
 * @param {String}   keyName
 * @param {Boolean}  isPublicProfile
 * @param {Boolean}  isEditable
 * @param {Boolean}  parentLoading
 * @param {Boolean}  showAiSummary
 * @param {Boolean}  showAiNoteSummary
 */
const ProfileBox = ({
  id,
  keyName,
  isEditable,
  isPublicProfile,
  loading: parentLoading,
  showAiSummary,
  showAiNoteSummary,
  ...props
}) => {
  const { loading: profileLoading, data, error, profileCompletion } = useProfile({ profileId: id, isPublicProfile });
  const loading = parentLoading || profileLoading;

  if ((!loading && !data) || error) {
    return <MessagesError messageId="Profile.ProfileBox.Error" />;
  }

  return (
    <Grid>
      <Grid.col start={1} end={13}>
        <Card>
          <ProfileBio
            data={data && data[keyName]}
            loading={loading}
            profileCompletion={profileCompletion}
            isEditable={isEditable}
            {...props}
          />
          <ProfileDisciplines
            data={data && data[keyName]}
            loading={loading}
            isEmpty={profileCompletion.disciplines.isEmpty}
            isEditable={isEditable}
            {...props}
          />
          <ProfileSkills
            data={data && data[keyName]}
            loading={loading}
            isEmpty={profileCompletion.skills.isEmpty}
            isEditable={isEditable}
            {...props}
          />
          {!isPublicProfile && (
            <ProfileContact
              data={data && data[keyName]}
              loading={loading}
              isEmpty={profileCompletion.info.isEmpty}
              isEditable={isEditable}
              {...props}
            />
          )}
        </Card>
      </Grid.col>
      {showAiSummary && (
        <Grid.col start={1} end={13}>
          <Card>
            <ProfileAiSummary
              profileId={data && data[keyName]?.slug}
              loading={loading}
              msgId="Missions.Applicants.ApplicantDetailsModal.LabelAISummary"
              showRatings={false}
              {...props}
            />
          </Card>
        </Grid.col>
      )}
      {showAiNoteSummary && (
        <Grid.col start={1} end={13}>
          <Card isAdmin>
            <ProfileNoteAiSummary
              profileId={data && data[keyName]?.slug}
              loading={loading}
              msgId="NotesSummary.AI.Title"
              showRatings={false}
              showRegenerateButton={false}
              {...props}
            />
          </Card>
        </Grid.col>
      )}
      <Grid.col start={1} end={13}>
        <Card>
          <Grid rowGap="1rem">
            <Grid.col start={1} end={13}>
              <ProfileWorkExperience
                profileId={data && data[keyName]?.id}
                data={data && data[keyName]?.workExperiences}
                loading={loading}
                isEmpty={profileCompletion.workExperiences.isEmpty}
                isEditable={isEditable}
                {...props}
              />
            </Grid.col>
            {!isPublicProfile && (
              <Grid.col start={1} end={13}>
                <ProfileResume
                  data={data && data[keyName]}
                  loading={loading}
                  isEmpty={profileCompletion.resume.isEmpty}
                  isEditable={isEditable}
                  {...props}
                />
              </Grid.col>
            )}
          </Grid>
        </Card>
      </Grid.col>
      <Grid.col start={1} end={13}>
        <Card>
          <ProfileEducation
            profileId={data && data[keyName]?.id}
            data={data && data[keyName]?.education}
            loading={loading}
            isEmpty={profileCompletion.education.isEmpty}
            isEditable={isEditable}
            {...props}
          />
        </Card>
      </Grid.col>
    </Grid>
  );
};

ProfileBox.defaultProps = {
  keyName: "profile",
  isPublicProfile: false,
  showAiSummary: false,
};

ProfileBox.propTypes = {
  id: PropTypes.string,
  keyName: PropTypes.string,
  isPublicProfile: PropTypes.bool,
  isEditable: PropTypes.bool,
  loading: PropTypes.bool,
  showAiSummary: PropTypes.bool,
  showAiNoteSummary: PropTypes.bool,
};

export default ProfileBox;
